import {Subscription} from 'rxjs';
import {ChangeDetectionStrategy, Component, effect, OnDestroy, OnInit, signal} from '@angular/core';
import {NgClass, NgOptimizedImage} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {
  DashboardStudyViewerControlsHeadOutlineComponent
} from '../dashboard-study-viewer-controls-head-outline/dashboard-study-viewer-controls-head-outline.component';
import {DashboardStudyViewerControlsSelectComponent} from '../dashboard-study-viewer-controls-select/dashboard-study-viewer-controls-select.component';
import {DashboardStudyViewerMode} from '../../symbols';
import {DashboardStudyViewerControllerService} from '../../services/dashboard-study-viewer-controller/dashboard-study-viewer-controller.service';
import {RtChoice} from '../../../rt-inputs/symbols/rt-inputs-types.symbols';
import {DashboardSeriesScanClass} from '../../../dashboard-new/types/dashboard.types';
import {DashboardViewRegion, DashboardViewRegionBodySegment} from '../../../dashboard-view-regions/types/dashboard-view-regions.types';
import {DashboardStudyViewerLoadingState} from '../../symbols/dashboard-study-viewer.symbols';

@Component({
  selector: 'app-dashboard-study-viewer-controls',
  templateUrl: './dashboard-study-viewer-controls.component.html',
  styleUrls: ['./dashboard-study-viewer-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DashboardStudyViewerControlsSelectComponent,
    NgOptimizedImage,
    DashboardStudyViewerControlsHeadOutlineComponent,
    NgClass,
  ],
})
export class DashboardStudyViewerControlsComponent implements OnInit, OnDestroy {
  public readonly currentLoadingStatus = this.studyViewerController.currentLoadingStatus;
  public readonly studyViewerMode = this.studyViewerController.studyViewerMode;
  public readonly renderedViewRegion = this.studyViewerController.renderedViewRegion;

  /**
   * The list of available loading states for the study viewer.
   */
  public readonly studyViewerLoadingStates = DashboardStudyViewerLoadingState;

  /** The list of available study viewer modes. */
  public readonly studyViewerModes = DashboardStudyViewerMode;

  /** The body segment of the rendered view region. */
  public readonly viewRegionBodySegments = DashboardViewRegionBodySegment;

  /** The control for the viewer mode selection. */
  public readonly viewerModeControl = new FormControl<DashboardStudyViewerMode>(DashboardStudyViewerMode.Surface);

  /** The control for the scan type selection. */
  public readonly scanTypeControl = new FormControl<DashboardSeriesScanClass>(DashboardSeriesScanClass.T1Brain);

  /** The list of viewer modes that will be displayed in the select input. */
  public readonly viewerModeChoices: RtChoice<DashboardStudyViewerMode, string>[] = [
    [DashboardStudyViewerMode.Surface, '3D'],
    [DashboardStudyViewerMode.Scan, 'Scan'],
  ];

  /** The last selected body segment to avoid unnecessary updates. */
  public lastSelectedBodySegment: DashboardViewRegion['body_segment'] = DashboardViewRegionBodySegment.Brain;

  /** The list of scan types for the brain view region. */
  public readonly brainScanTypeChoices: RtChoice<DashboardSeriesScanClass, string>[] = [
    [DashboardSeriesScanClass.T1Brain, 'T1'],
    [DashboardSeriesScanClass.T2, 'T2'],
    [DashboardSeriesScanClass.Flair, 'Flair'],
    [DashboardSeriesScanClass.Tof, 'Tof'],
    [DashboardSeriesScanClass.Swi, 'Swi'],
  ];

  /** The list of scan types for the whole body view region. */
  public readonly wholeBodyScanTypeChoices: RtChoice<DashboardSeriesScanClass, string>[] = [[DashboardSeriesScanClass.T1WholeBody, 'T1']];

  /** The list of scan types that will be displayed in the select input. */
  public readonly scanTypeChoices = signal<RtChoice<DashboardSeriesScanClass, string>[]>(this.brainScanTypeChoices);

  /** Component subscriptions. Will be unsubscribed on component destruction. */
  public readonly subscription = new Subscription();

  constructor(private studyViewerController: DashboardStudyViewerControllerService) {
    effect(() => {
      const studyViewerMode = this.studyViewerController.studyViewerMode();
      const selectedBaseScanClass = this.studyViewerController.selectedBaseScanClass();
      const renderedViewRegion = this.studyViewerController.renderedViewRegion();

      // Update the controls if the mode was changed outside the component.
      if (studyViewerMode === DashboardStudyViewerMode.Surface || studyViewerMode === DashboardStudyViewerMode.SurfaceWithProfile) {
        this.viewerModeControl.setValue(DashboardStudyViewerMode.Surface, {emitEvent: false});
      } else if (studyViewerMode === DashboardStudyViewerMode.Scan || studyViewerMode === DashboardStudyViewerMode.ScanWithDraw) {
        this.viewerModeControl.setValue(DashboardStudyViewerMode.Scan, {emitEvent: false});
      }

      // Update base scan class choices based on the rendered view region.
      // Brain and full body have different base scan classes (and the related scan type choices).
      if (this.lastSelectedBodySegment !== renderedViewRegion.body_segment) {
        if (renderedViewRegion.body_segment === DashboardViewRegionBodySegment.Brain) {
          this.scanTypeChoices.set(this.brainScanTypeChoices);
          this.lastSelectedBodySegment = DashboardViewRegionBodySegment.Brain;
        } else if (renderedViewRegion.body_segment === DashboardViewRegionBodySegment.FullBody) {
          this.scanTypeChoices.set(this.wholeBodyScanTypeChoices);
          this.lastSelectedBodySegment = DashboardViewRegionBodySegment.FullBody;
        }
      }

      // Update the scan type control value if the selected base scan class was changed.
      if (selectedBaseScanClass !== this.scanTypeControl.value) {
        this.scanTypeControl.setValue(selectedBaseScanClass, {emitEvent: false});
      }
    });
  }

  ngOnInit(): void {
    // Update the viewer mode when the related control value changes.
    this.subscription.add(
      this.viewerModeControl.valueChanges.subscribe((mode: DashboardStudyViewerMode) => {
        this.studyViewerController.updateViewerMode(mode);
      })
    );

    // Update the scan type when the related control value changes.
    this.subscription.add(
      this.scanTypeControl.valueChanges.subscribe((scanClass: DashboardSeriesScanClass) => {
        this.studyViewerController.updateBaseScanClass(scanClass);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Resets the current view to the initial state by moving the inspect point to the initial position,
   * resetting the zoom level, and resetting the rotation.
   */
  public resetCurrentView(): void {
    this.studyViewerController.resetCurrentView();
  }

  /**
   * Enters the zoomed view mode, allowing users to zoom in on specific areas of the study.
   */
  public enterZoomedView(isActive: boolean): void {
    if (isActive) {
      this.studyViewerController.isZoomedView.set(true);
    }
  }
}
